import React, { useState } from "react"
import { Modal, Button, Text, ProgressIndicator } from "@quorumus/plum"
import { Spoiler, Stack, Textarea, Box } from "@mantine/core"
import { SparklesFarFAIcon, ArrowsRotateFAIcon } from "@quorumus/plum/dist/components/Icons"
import { PersonalizationProfileSelect } from "@/components/PersonalizationProfileSelect/PersonalizationProfileSelect"
import { usePersonalizedSummaryGeneration, RateLimitError } from "./hooks/usePersonalizedSummaryGeneration"
import classes from "./PersonalizedSummary.module.css"
import { usePersonalizedSummary } from "./hooks/usePersonalizedSummary"
import { usePersonalizedSummaryApplication } from "./hooks/usePersonalizedSummaryApplication"
import Markdown from "@/components/CopilotAnalyze/Message/Markdown"
import { FeedbackActions } from "./FeedbackActions"
import { useNavigate } from "react-router-dom"
import { useFeedbackMutation } from "./hooks/useFeedbackMutation"
import useNotification from "./hooks/useNotification"

type ModalState = "closed" | "profile-selection" | "summary-generated" | "confirm-override"

// export const PersonalizedSummary: React.FC<PersonalizedSummaryProps> = ({ boardId, profileId, billId }) => {
export const PersonalizedSummary = ({ boardId, profileId, billId, nextBillId }) => {
    const navigate = useNavigate()
    const [modalState, setModalState] = useState<ModalState>("closed")
    const [selectedProfileId, setSelectedProfileId] = useState<string | null>(profileId)
    const [feedbackText, setFeedbackText] = useState("")
    const [showFeedbackForm, setShowFeedbackForm] = useState<"positive" | "negative" | null>(null)
    const [rateLimitError, setRateLimitError] = useState<RateLimitError | null>(null)

    const boardHasProfile = boardId && !!selectedProfileId

    const generateMutation = usePersonalizedSummaryGeneration()
    const applyMutation = usePersonalizedSummaryApplication()
    const feedbackMutation = useFeedbackMutation()
    const notification = useNotification()
    const personalizationQuery = usePersonalizedSummary({
        billId,
        boardId,
        profileId: selectedProfileId,
        nextBillId,
        enabled: boardHasProfile,
    })

    // Check for rate limit errors from mutations
    const currentRateLimitError =
        (generateMutation.error instanceof RateLimitError ? generateMutation.error : null) ||
        (applyMutation.error instanceof RateLimitError ? applyMutation.error : null) ||
        rateLimitError

    // Check if there was an error indicating no profile configured for the board
    // This handles both error responses and successful responses indicating no profile
    // IMPORTANT: Board-specific behavior - summaries are isolated per board
    const isProfileNotFoundError =
        (personalizationQuery.error &&
            typeof personalizationQuery.error === "object" &&
            "message" in personalizationQuery.error &&
            typeof personalizationQuery.error.message === "string" &&
            personalizationQuery.error.message.includes("personalization profile configured")) ||
        // Also check for board-specific "no profile" responses (data=null for boards without profiles)
        (boardId &&
            personalizationQuery.data === null &&
            !personalizationQuery.isLoading &&
            !personalizationQuery.error)

    const handleGenerateClick = () => {
        // If in AI Bill Tracking context but no profile assigned, navigate to board config
        if (boardId && !selectedProfileId) {
            navigate(`/ai_bill_tracker/${boardId}/edit/?step=configure`)
            return
        }

        if (boardId && selectedProfileId) {
            generateMutation.mutate(
                { billId, profileId: selectedProfileId, boardId },
                {
                    onSuccess: (data) => {
                        applyMutation.mutate(
                            {
                                boardId,
                                billId,
                                summary: data.summary,
                                profileId: selectedProfileId,
                            },
                            {
                                onError: (error) => {
                                    if (error instanceof RateLimitError) {
                                        setRateLimitError(error)
                                        setModalState("closed")
                                    } else {
                                        console.error("Failed to apply summary:", error)
                                    }
                                },
                            },
                        )
                    },
                    onError: (error) => {
                        if (error instanceof RateLimitError) {
                            setRateLimitError(error)
                            setModalState("closed")
                        } else {
                            console.error("Failed to generate summary:", error)
                        }
                    },
                },
            )
        } else {
            // Bill Profile: Show modal as before
            setModalState("profile-selection")
        }
    }

    const handleCloseModal = () => {
        setModalState("closed")
        generateMutation.reset()
    }

    const handleProfileGenerate = async () => {
        if (!selectedProfileId) return

        // Clear any previous rate limit errors
        setRateLimitError(null)
        setModalState("summary-generated")

        generateMutation.mutate({ billId, profileId: selectedProfileId, boardId })
    }

    const handleApplySummary = async () => {
        if (!generateMutation.data || !selectedProfileId) return

        // Show confirmation modal if there's already a summary
        if (modalState !== "confirm-override" && personalizationQuery.data?.summary) {
            setModalState("confirm-override")
            return
        }

        try {
            await applyMutation.mutateAsync({
                boardId,
                billId,
                summary: generateMutation.data.summary,
                profileId: selectedProfileId,
            })
            handleCloseModal()
            personalizationQuery.refetch()
        } catch (error) {
            console.error("Failed to apply summary:", error)
            handleCloseModal() // Close modal regardless of error type
        }
    }

    const handleFeedbackClick = (type: "positive" | "negative") => {
        setShowFeedbackForm(type)
        setFeedbackText("")
    }

    const handleSubmitFeedback = async () => {
        if (!showFeedbackForm) return

        const summary = personalizationQuery.data?.summary || generateMutation.data?.summary || ""

        feedbackMutation.mutate({
            summary,
            billId,
            profileId: selectedProfileId || "",
            feedback_type: showFeedbackForm,
            feedback_text: feedbackText,
        })

        if (showFeedbackForm === "positive") {
            handleApplySummary()
        } else {
            setModalState("profile-selection")
            generateMutation.reset()
        }

        notification.feedbackReceived()
        setShowFeedbackForm(null)
        setFeedbackText("")
    }

    if (personalizationQuery.isFetching || generateMutation.isLoading || applyMutation.isLoading) {
        return (
            <div className={classes.initialContainer}>
                <ProgressIndicator size="sm" />
                {boardId ? (
                    <Text size="sm">
                        {selectedProfileId ? "Generating personalized summary..." : "Checking board configuration..."}
                    </Text>
                ) : (
                    <Text size="sm">Loading summary...</Text>
                )}
            </div>
        )
    }

    return (
        <>
            {/* Rate limit error display */}
            {currentRateLimitError && (
                <div
                    className={classes.errorContainer}
                    style={{
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffeaa7",
                        borderRadius: "4px",
                        padding: "12px",
                        marginBottom: "16px",
                    }}
                >
                    <Text size="sm" style={{ color: "#856404", fontWeight: 500 }}>
                        You've reached the limit of 10 summaries per hour for this bill.
                    </Text>
                    <Text size="sm" style={{ color: "#856404", marginTop: "4px" }}>
                        Please try again at{" "}
                        <strong>
                            {currentRateLimitError.retryAtTime.toLocaleString("en-US", {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true,
                            })}
                        </strong>{" "}
                        ({Math.ceil(currentRateLimitError.retryAfterSeconds / 60)} minute
                        {Math.ceil(currentRateLimitError.retryAfterSeconds / 60) !== 1 ? "s" : ""} from now).
                    </Text>
                </div>
            )}

            <div className={classes.initialContainer}>
                {personalizationQuery.data?.summary ? (
                    <>
                        <Spoiler maxHeight={120} showLabel="See more" hideLabel="See less">
                            <Stack spacing="xs">
                                <Markdown>{personalizationQuery.data.summary}</Markdown>
                            </Stack>
                        </Spoiler>
                        <div className={classes.summaryFooter}>
                            {personalizationQuery.data.createdAt && (
                                <Text size="sm" color="dimmed">
                                    {`Generated on ${new Intl.DateTimeFormat("en-US", {
                                        day: "numeric",
                                        month: "short",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: false,
                                    }).format(
                                        new Date(personalizationQuery.data.createdAt),
                                    )} by "${personalizationQuery.data.profileName}"`}
                                </Text>
                            )}
                            <Button
                                variant="secondary"
                                size="sm"
                                leftIcon={ArrowsRotateFAIcon}
                                onClick={handleGenerateClick}
                                className={classes.generateButton}
                                disabled={!!currentRateLimitError}
                                title={
                                    currentRateLimitError
                                        ? `Rate limit exceeded. Try again at ${currentRateLimitError.retryAtTime.toLocaleTimeString()}`
                                        : undefined
                                }
                            >
                                Regenerate
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        <Text size="sm" className={classes.initialText}>
                            {!boardHasProfile
                                ? "No AI Personalized profile is configured for this tracking board. Configure a profile to generate personalized summaries."
                                : isProfileNotFoundError
                                  ? "This tracking board doesn't have an AI Personalized profile configured."
                                  : boardId
                                    ? "No personalized summary available for this bill in this tracking board."
                                    : "It looks like you don't have a personalized summary yet."}
                        </Text>
                        <Button
                            variant="primary"
                            size="sm"
                            leftIcon={!boardHasProfile || isProfileNotFoundError ? undefined : SparklesFarFAIcon}
                            onClick={handleGenerateClick}
                            className={classes.generateButton}
                            data-plum-id="generate-personalized-ai-summary-initial"
                            disabled={!!currentRateLimitError}
                            title={
                                currentRateLimitError
                                    ? `Rate limit exceeded. Try again at ${currentRateLimitError.retryAtTime.toLocaleTimeString()}`
                                    : undefined
                            }
                        >
                            {!boardHasProfile || isProfileNotFoundError ? "Configure Profile" : "Generate"}
                        </Button>
                    </>
                )}
                <div className={classes.divider} />
            </div>
            {/* Confirmation Modal */}
            <Modal
                opened={modalState === "confirm-override"}
                onClose={handleCloseModal}
                title=""
                size="360px"
                withCloseButton={false}
                centered
                classNames={{ header: classes.modalHeader }}
                className={classes.modal}
            >
                <div className={classes.confirmationModalContent}>
                    <div className={classes.confirmationIcon}>
                        <ArrowsRotateFAIcon />
                    </div>
                    <div className={classes.confirmationText}>
                        <Text className={classes.confirmationTitle}>Override existing Personalized Summary?</Text>
                        <Text className={classes.confirmationDescription}>
                            This personalized summary will override
                            <br />
                            the one generated on{" "}
                            {personalizationQuery.data?.createdAt &&
                                new Intl.DateTimeFormat("en-US", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                }).format(new Date(personalizationQuery.data.createdAt))}
                            .
                            <br />
                            Do you wish to continue?
                        </Text>
                    </div>
                    <div className={classes.confirmationActions}>
                        <Button variant="secondary" size="sm" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button variant="accent" size="sm" onClick={handleApplySummary}>
                            Override
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Profile Selection Modal */}
            <Modal
                opened={modalState === "profile-selection" || modalState === "summary-generated"}
                onClose={handleCloseModal}
                title="Select a personalization"
                size="600px"
                classNames={{ header: classes.modalHeader }}
                className={classes.modal}
            >
                {/* Modal content only shows for Bill Profile context (no boardId) */}
                {!boardId && (modalState === "profile-selection" || modalState === "summary-generated") && (
                    <div className={classes.modalContent}>
                        <div className={classes.profileSelectRow}>
                            <div className={classes.profileSelectContainer}>
                                <PersonalizationProfileSelect
                                    label="Personalization"
                                    value={selectedProfileId?.toString()}
                                    onChange={setSelectedProfileId}
                                    onNewProfile={handleCloseModal}
                                />
                            </div>
                            <Button
                                variant="primary"
                                size="sm"
                                leftIcon={SparklesFarFAIcon}
                                onClick={handleProfileGenerate}
                                loading={generateMutation.isLoading}
                                disabled={!!currentRateLimitError}
                                title={
                                    currentRateLimitError
                                        ? `Rate limit exceeded. Try again at ${currentRateLimitError.retryAtTime.toLocaleTimeString()}`
                                        : undefined
                                }
                            >
                                Generate
                            </Button>
                        </div>
                        {generateMutation.data && (
                            <>
                                <div className={classes.divider} />
                                <Text className={classes.previewTitle}>Preview</Text>
                                <div className={classes.summaryPreview}>
                                    <div className={classes.summaryContent}>
                                        <div className={classes.summaryTitle}>Personalized Summary</div>
                                        <Spoiler maxHeight={120} showLabel="See more" hideLabel="See less">
                                            <Stack spacing="xs">
                                                <Markdown>{generateMutation.data.summary}</Markdown>
                                                {generateMutation.data.createdAt && (
                                                    <Text size="sm" color="dimmed">
                                                        Updated:{" "}
                                                        {new Date(generateMutation.data.createdAt).toLocaleDateString()}
                                                    </Text>
                                                )}
                                            </Stack>
                                        </Spoiler>
                                    </div>
                                </div>
                                <div className={classes.feedbackRow}>
                                    <div className={classes.feedbackButtons}>
                                        <FeedbackActions
                                            summary={generateMutation?.data?.summary}
                                            billId={billId}
                                            profileId={selectedProfileId}
                                            onFeedbackClick={handleFeedbackClick}
                                            selectedFeedback={showFeedbackForm}
                                        />
                                    </div>

                                    {!showFeedbackForm && (
                                        <Button
                                            variant="accent"
                                            size="sm"
                                            onClick={handleApplySummary}
                                            loading={applyMutation.isLoading}
                                            disabled={applyMutation.isLoading}
                                        >
                                            Submit and Apply
                                        </Button>
                                    )}
                                </div>

                                {/* Inline Feedback Form */}
                                {showFeedbackForm && (
                                    <>
                                        <Stack spacing="xs">
                                            <Text size="md" weight={600}>
                                                {showFeedbackForm === "positive"
                                                    ? "What was helpful about this summary?"
                                                    : "How could this summary be improved?"}
                                            </Text>

                                            <Textarea
                                                value={feedbackText}
                                                onChange={(event) => setFeedbackText(event.currentTarget.value)}
                                                minRows={3}
                                                maxRows={6}
                                                autosize
                                            />

                                            <Box style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                                                <Button
                                                    variant="filled"
                                                    onClick={handleSubmitFeedback}
                                                    loading={feedbackMutation.isLoading}
                                                >
                                                    {showFeedbackForm === "positive"
                                                        ? "Submit and Apply"
                                                        : "Submit and Restart"}
                                                </Button>
                                            </Box>
                                        </Stack>
                                    </>
                                )}
                            </>
                        )}
                    </div>
                )}
            </Modal>
        </>
    )
}
